import { useEffect, useMemo } from 'react';
import { useEmployeeProfilePhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { useRedirectUrlContext } from './redirectUrlContext';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useEmployeeMode } from '@pflegenavi/shared-frontend/employee-mode';
import { useIsNursingHomeScope, useScope } from './ScopeContext';
import type { EmployeeRole } from './employeeRoles';
import {
  ADMIN_OR_MANAGER_ROLES,
  checkIfUserHasAccess,
  hasRoleInAnyNursingHome,
  isGlobalTenantAdmin,
  useEmployeeHasNursingHomeRole,
} from './employeeRoles';

const ADMIN_ONLY: EmployeeRole[] = ['admin'];

export function useEmployeeIsNursingHomeAdminOrManager(
  enabled = true
): boolean {
  const { selectedNursingHome } = useScope();

  return useEmployeeHasNursingHomeRole(
    selectedNursingHome?.id,
    ADMIN_OR_MANAGER_ROLES,
    enabled
  );
}

// `undefined` until the profile arrives, so callers can tell "not allowed"
// apart from "not known yet". Deliberately independent of the selected nursing
// home: the role is held per home, but the wider scopes are not, so the tabs
// must not come and go as the user switches homes (ENG-2503).
// Legacy tenants grant access per nursing home only, so a legal entity view
// would mean something narrower there - and their profile synthesizes a manager
// role for every employee, which the `use_new_authorization` gate keeps out.
export function useCanLeaveNursingHomeScope(): boolean | undefined {
  const { data: employeeProfile } = useEmployeeProfilePhoenix({});

  if (!employeeProfile) {
    return undefined;
  }

  return (
    hasRoleInAnyNursingHome(employeeProfile, ADMIN_OR_MANAGER_ROLES) &&
    Boolean(employeeProfile.use_new_authorization)
  );
}

// The tenant scope spans every legal entity, so only a global tenant admin may
// enter it. Same `undefined`-while-loading contract as
// {@link useCanLeaveNursingHomeScope}.
export function useCanUseTenantScope(): boolean | undefined {
  const { data: employeeProfile } = useEmployeeProfilePhoenix({});

  if (!employeeProfile) {
    return undefined;
  }

  return (
    isGlobalTenantAdmin(employeeProfile) &&
    Boolean(employeeProfile.use_new_authorization)
  );
}

export function useEmployeeIsNursingHomeAdmin(enabled = true): boolean {
  const { selectedNursingHome } = useScope();

  return useEmployeeHasNursingHomeRole(
    selectedNursingHome?.id,
    ADMIN_ONLY,
    enabled
  );
}

export function useSelectedEmployeeIds(): string[] | undefined {
  const isNursingHomeAdminOrManager = useEmployeeIsNursingHomeAdminOrManager();
  // The employee selector only exists at nursing home scope, so a selection
  // left over from one would silently narrow a whole legal entity with no
  // visible control to clear it. Employees without the wider role are pinned to
  // themselves at every scope, which is a restriction rather than a filter.
  const isNursingHomeScope = useIsNursingHomeScope();

  const { user } = useAuthentication();

  const { selectedEmployeeId } = useEmployeeMode();
  return useMemo(() => {
    if (isNursingHomeAdminOrManager) {
      return isNursingHomeScope && selectedEmployeeId
        ? [selectedEmployeeId]
        : undefined;
    }
    return user?.userId ? [user.userId] : undefined;
  }, [
    isNursingHomeAdminOrManager,
    isNursingHomeScope,
    selectedEmployeeId,
    user?.userId,
  ]);
}

export function useRedirectUrlContextRoles(url: string): void {
  const { data: employeeProfile } = useEmployeeProfilePhoenix({});

  const { setRedirectUrl } = useRedirectUrlContext();

  useEffect(() => {
    setRedirectUrl(() => (nursingHomeId: string) => {
      return checkIfUserHasAccess(employeeProfile, nursingHomeId)
        ? url
        : nhAppMainPages.DASHBOARD;
    });
  }, [employeeProfile, setRedirectUrl, url]);
}
